import Phaser from 'phaser';
import { getPlayerBonuses } from '../game/playerBonuses';
import { isHell1StageCleared, recordHell1StageClear } from '../game/hell1Progress';
import { getHell1ArenaConfig } from '../game/hell1Stages';

/** 한 구역 안의 연속 단계(웨이브). */
export type ArenaWaveSpec = {
  killTarget: number;
  enemySpawnMs: number;
  fireRateMs: number;
  enemySpeed: number;
};

export type ArenaStageConfig = {
  killTarget: number;
  regionLabel: string;
  playerColor: number;
  enemyColor: number;
  projectileColor: number;
  cameraBgColor: string;
  fireRateMs: number;
  enemySpawnMs: number;
  clearTitle: string;
  clearSubtitle: string;
  /** 설정 시 클리어 후 이 씬으로 전환 */
  nextSceneKey?: string;
  transitionDelayMs: number;
  /** 최대 체력 (미설정 시 기본값) */
  playerMaxHp?: number;
  /** true면 레지스트리의 영구 보너스(스테이지 보상) 반영 — 지옥 1층 전투 등 */
  applyPersistentBonuses?: boolean;
  /** 지옥 1층 스테이지 식별 — 클리어 기록용 */
  hellStageId?: string;
  /** 적 추적 이동 속도 (미설정 시 기본값) */
  enemySpeed?: number;
  /** 설정 시 단계별로 목표 처치·스폰·발사·적 속도가 바뀌며 한 번에 연속 진행 */
  waves?: ArenaWaveSpec[];
};

const PLAYER_SPEED = 240;
const ENEMY_SPEED = 78;
const PROJECTILE_SPEED = 440;
/** 발사체를 피격판 안쪽이 아니라 적 쪽으로 약간 띄워 생성 */
const PROJECTILE_SPAWN_OFFSET = 22;
const DEFAULT_PLAYER_MAX_HP = 5;
/** 적과 겹친 뒤 다시 데미지 받기까지 (밀리초) */
const HIT_INVULN_MS = 900;

/**
 * 공통 서바이버 전투 화면. 스테이지별 설정은 서브클래스에서 넘깁니다.
 */
export class CombatArenaScene extends Phaser.Scene {
  protected stage: ArenaStageConfig;

  private player!: Phaser.GameObjects.Rectangle;
  private playerBody!: Phaser.Physics.Arcade.Body;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private keyW!: Phaser.Input.Keyboard.Key;
  private keyA!: Phaser.Input.Keyboard.Key;
  private keyS!: Phaser.Input.Keyboard.Key;
  private keyD!: Phaser.Input.Keyboard.Key;

  private enemies!: Phaser.Physics.Arcade.Group;
  private projectiles!: Phaser.Physics.Arcade.Group;

  private fireAccumulator = 0;
  private spawnAccumulator = 0;
  private kills = 0;
  private cleared = false;
  /** HP가 0이 된 뒤 재시작 대기 */
  private deathPending = false;
  private playerMaxHp = DEFAULT_PLAYER_MAX_HP;
  private playerHp = DEFAULT_PLAYER_MAX_HP;
  private invulnerableUntil = 0;

  private hud!: Phaser.GameObjects.Text;
  private abandonBtn?: Phaser.GameObjects.Text;
  private abandonOverlay: Phaser.GameObjects.GameObject[] = [];
  private abandonFrozenPhysics = false;

  private effectivePlayerSpeed = PLAYER_SPEED;
  private effectiveFireRateMs = 380;

  private waveMode = false;
  private waveIndex = 0;
  private waveKills = 0;
  private activeWaveKillTarget = 0;
  private activeEnemySpawnMs = 1000;
  private activeEnemySpeed = ENEMY_SPEED;
  private waveTransitioning = false;
  private headerLabel!: Phaser.GameObjects.Text;

  protected constructor(key: string, stage: ArenaStageConfig) {
    super(key);
    this.stage = stage;
  }

  /** 패배 시 `scene.restart`에 넘길 데이터 — 지옥 스테이지 등에서 재정의 */
  protected getSceneRestartPayload(): object | undefined {
    return undefined;
  }

  create(): void {
    const { width, height } = this.scale;

    this.cameras.main.setBackgroundColor(this.stage.cameraBgColor);
    this.physics.world.setBounds(0, 0, width, height);
    this.physics.resume();

    this.deathPending = false;
    this.abandonFrozenPhysics = false;
    this.invulnerableUntil = 0;

    const baseMaxHp = this.stage.playerMaxHp ?? DEFAULT_PLAYER_MAX_HP;
    let maxHp = baseMaxHp;
    let speed = PLAYER_SPEED;

    if (this.stage.applyPersistentBonuses === true) {
      const b = getPlayerBonuses(this.registry);
      maxHp = baseMaxHp + b.maxHpFlat;
      speed = PLAYER_SPEED * b.speedMult;
    }

    this.effectivePlayerSpeed = speed;
    this.playerMaxHp = maxHp;
    this.playerHp = maxHp;

    if (this.stage.waves && this.stage.waves.length > 0) {
      this.waveMode = true;
      this.applyWaveIndex(0);
    } else {
      let fireMs = this.stage.fireRateMs;
      if (this.stage.applyPersistentBonuses === true) {
        const b = getPlayerBonuses(this.registry);
        fireMs = Math.max(110, Math.round(this.stage.fireRateMs * b.fireRateMult));
      }
      this.effectiveFireRateMs = fireMs;
      this.activeWaveKillTarget = this.stage.killTarget;
      this.activeEnemySpawnMs = this.stage.enemySpawnMs;
      this.activeEnemySpeed = this.stage.enemySpeed ?? ENEMY_SPEED;
    }

    const player = this.add.rectangle(width / 2, height / 2, 28, 28, this.stage.playerColor);
    this.physics.add.existing(player);
    const pb = player.body as Phaser.Physics.Arcade.Body;
    pb.setCollideWorldBounds(true);
    pb.setDrag(900);
    pb.setMaxVelocity(this.effectivePlayerSpeed);
    this.player = player;
    this.playerBody = pb;

    this.enemies = this.physics.add.group();
    this.projectiles = this.physics.add.group();

    this.physics.add.overlap(
      this.projectiles,
      this.enemies,
      (projGo, enemyGo) =>
        this.onProjectileHitEnemy(
          projGo as Phaser.GameObjects.Rectangle,
          enemyGo as Phaser.GameObjects.Rectangle,
        ),
      undefined,
      this,
    );

    this.physics.add.overlap(
      this.player,
      this.enemies,
      () => this.onPlayerHitEnemy(),
      () =>
        !this.cleared &&
        !this.deathPending &&
        !this.abandonFrozenPhysics &&
        this.time.now >= this.invulnerableUntil,
      this,
    );

    const kb = this.input.keyboard;
    if (!kb) {
      throw new Error('Keyboard input unavailable');
    }
    this.cursors = kb.createCursorKeys();
    this.keyW = kb.addKey(Phaser.Input.Keyboard.KeyCodes.W);
    this.keyA = kb.addKey(Phaser.Input.Keyboard.KeyCodes.A);
    this.keyS = kb.addKey(Phaser.Input.Keyboard.KeyCodes.S);
    this.keyD = kb.addKey(Phaser.Input.Keyboard.KeyCodes.D);

    this.headerLabel = this.add
      .text(width / 2, 10, this.stage.regionLabel, {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '13px',
        color: '#9a9ab8',
      })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setDepth(100);

    this.hud = this.add
      .text(14, 28, '', {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '15px',
        color: '#e8e8ee',
      })
      .setScrollFactor(0)
      .setDepth(100);

    this.add
      .text(width / 2, height - 26, '이동: WASD 또는 방향키', {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '13px',
        color: '#7a7a9a',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(100);

    this.abandonBtn = this.add
      .text(width - 14, 52, '포기', {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '14px',
        color: '#e8c4c4',
        backgroundColor: '#3d2528cc',
        padding: { left: 10, right: 10, top: 6, bottom: 6 },
      })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(158)
      .setInteractive({ useHandCursor: true });

    this.abandonBtn.on('pointerover', () => this.abandonBtn?.setAlpha(1));
    this.abandonBtn.on('pointerout', () => this.abandonBtn?.setAlpha(0.92));
    this.abandonBtn.on('pointerdown', () => this.openAbandonConfirm());
    this.abandonBtn.setAlpha(0.92);

    this.updateWaveHeader();
    this.refreshHud();
  }

  update(_time: number, delta: number): void {
    if (this.cleared || this.deathPending) return;
    /* 물리 일시정지만으로는 스폰·이동 타이머가 멈추지 않음 — 포기 창이 열린 동안 전투 로직 중단 */
    if (this.abandonFrozenPhysics) return;
    if (this.waveTransitioning) return;

    this.movePlayer(delta);
    this.stepFire(delta);
    this.stepSpawn(delta);
    this.chasePlayerOnEnemies();
    this.cullProjectiles();
  }

  private movePlayer(delta: number): void {
    const factor = delta / 1000;
    let vx = 0;
    let vy = 0;
    if (this.cursors.left.isDown || this.keyA.isDown) vx -= 1;
    if (this.cursors.right.isDown || this.keyD.isDown) vx += 1;
    if (this.cursors.up.isDown || this.keyW.isDown) vy -= 1;
    if (this.cursors.down.isDown || this.keyS.isDown) vy += 1;

    if (vx !== 0 || vy !== 0) {
      const len = Math.hypot(vx, vy) || 1;
      this.playerBody.setVelocity(
        (vx / len) * this.effectivePlayerSpeed,
        (vy / len) * this.effectivePlayerSpeed,
      );
    } else {
      const damp = Math.exp(-12 * factor);
      this.playerBody.setVelocity(this.playerBody.velocity.x * damp, this.playerBody.velocity.y * damp);
    }
  }

  private stepFire(delta: number): void {
    const quota = this.waveMode ? this.waveKills : this.kills;
    const target = this.waveMode ? this.activeWaveKillTarget : this.stage.killTarget;
    if (quota >= target) return;
    this.fireAccumulator += delta;
    if (this.fireAccumulator < this.effectiveFireRateMs) return;
    this.fireAccumulator = 0;
    this.fireProjectile();
  }

  private stepSpawn(delta: number): void {
    const quota = this.waveMode ? this.waveKills : this.kills;
    const target = this.waveMode ? this.activeWaveKillTarget : this.stage.killTarget;
    if (quota >= target) return;
    const spawnMs = this.waveMode ? this.activeEnemySpawnMs : this.stage.enemySpawnMs;
    this.spawnAccumulator += delta;
    if (this.spawnAccumulator < spawnMs) return;
    this.spawnAccumulator = 0;
    this.spawnEnemyFromEdge();
  }

  private chasePlayerOnEnemies(): void {
    const px = this.player.x;
    const py = this.player.y;
    for (const obj of this.enemies.getChildren()) {
      const enemy = obj as Phaser.GameObjects.Rectangle;
      if (!enemy.active) continue;
      const body = enemy.body as Phaser.Physics.Arcade.Body | undefined;
      if (!body) continue;
      const spd = this.waveMode ? this.activeEnemySpeed : this.stage.enemySpeed ?? ENEMY_SPEED;
      const angle = Phaser.Math.Angle.Between(enemy.x, enemy.y, px, py);
      body.setVelocity(Math.cos(angle) * spd, Math.sin(angle) * spd);
    }
  }

  private findNearestEnemy(): Phaser.GameObjects.Rectangle | null {
    let best: Phaser.GameObjects.Rectangle | null = null;
    let bestDist = Infinity;
    const px = this.player.x;
    const py = this.player.y;

    for (const obj of this.enemies.getChildren()) {
      const enemy = obj as Phaser.GameObjects.Rectangle;
      if (!enemy.active) continue;
      const d = Phaser.Math.Distance.Between(px, py, enemy.x, enemy.y);
      if (d < bestDist) {
        bestDist = d;
        best = enemy;
      }
    }
    return best;
  }

  private fireProjectile(): void {
    const target = this.findNearestEnemy();
    if (!target) return;

    const dx = target.x - this.player.x;
    const dy = target.y - this.player.y;
    const len = Math.hypot(dx, dy) || 1;
    const nx = dx / len;
    const ny = dy / len;
    const vx = nx * PROJECTILE_SPEED;
    const vy = ny * PROJECTILE_SPEED;

    const ox = this.player.x + nx * PROJECTILE_SPAWN_OFFSET;
    const oy = this.player.y + ny * PROJECTILE_SPAWN_OFFSET;

    const proj = this.add.rectangle(ox, oy, 10, 10, this.stage.projectileColor);
    this.physics.add.existing(proj);
    const body = proj.body as Phaser.Physics.Arcade.Body;
    body.setVelocity(vx, vy);
    body.setAllowGravity(false);
    this.projectiles.add(proj);

    this.time.delayedCall(2800, () => {
      if (proj.active) proj.destroy();
    });
  }

  private spawnEnemyFromEdge(): void {
    const { width, height } = this.scale;
    const pad = 24;
    const edge = Phaser.Math.Between(0, 3);
    let x = 0;
    let y = 0;
    switch (edge) {
      case 0:
        x = Phaser.Math.Between(pad, width - pad);
        y = pad;
        break;
      case 1:
        x = width - pad;
        y = Phaser.Math.Between(pad, height - pad);
        break;
      case 2:
        x = Phaser.Math.Between(pad, width - pad);
        y = height - pad;
        break;
      default:
        x = pad;
        y = Phaser.Math.Between(pad, height - pad);
        break;
    }

    const enemy = this.add.rectangle(x, y, 26, 26, this.stage.enemyColor);
    this.physics.add.existing(enemy);
    const body = enemy.body as Phaser.Physics.Arcade.Body;
    body.setAllowGravity(false);
    this.enemies.add(enemy);
  }

  private onProjectileHitEnemy(
    projectile: Phaser.GameObjects.Rectangle,
    enemy: Phaser.GameObjects.Rectangle,
  ): void {
    if (!projectile.active || !enemy.active) return;
    projectile.destroy();
    enemy.destroy();
    if (this.waveMode && this.stage.waves?.length) {
      this.waveKills += 1;
      this.refreshHud();
      if (this.waveKills >= this.activeWaveKillTarget) {
        if (this.waveIndex < this.stage.waves.length - 1) {
          this.beginWaveTransition();
        } else {
          this.triggerClear();
        }
      }
    } else {
      this.kills += 1;
      this.refreshHud();
      if (this.kills >= this.stage.killTarget) {
        this.triggerClear();
      }
    }
  }

  private onPlayerHitEnemy(): void {
    if (this.cleared || this.deathPending || this.abandonFrozenPhysics) return;
    const now = this.time.now;
    if (now < this.invulnerableUntil) return;

    this.invulnerableUntil = now + HIT_INVULN_MS;
    this.playerHp -= 1;
    this.cameras.main.flash(160, 240, 100, 90);
    this.refreshHud();

    this.tweens.killTweensOf(this.player);
    this.player.setAlpha(1);
    this.tweens.add({
      targets: this.player,
      alpha: 0.35,
      duration: 70,
      yoyo: true,
      repeat: 4,
      onComplete: () => this.player.setAlpha(1),
    });

    if (this.playerHp > 0) return;

    this.deathPending = true;
    this.physics.pause();
    this.time.delayedCall(520, () => {
      const payload = this.getSceneRestartPayload();
      if (payload !== undefined) {
        this.scene.restart(payload);
      } else {
        this.scene.restart();
      }
    });
  }

  private triggerClear(): void {
    if (this.cleared) return;
    this.cleared = true;
    this.dismissAbandonConfirm(false);
    this.abandonBtn?.setVisible(false);
    this.physics.pause();

    const sid = this.stage.hellStageId;
    let grantRewardPick = false;
    if (sid) {
      grantRewardPick = !isHell1StageCleared(this.registry, sid);
      recordHell1StageClear(this.registry, sid);
    }

    const { width, height } = this.scale;
    this.add
      .text(width / 2, height / 2 - 20, this.stage.clearTitle, {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '32px',
        color: '#f5f5fa',
      })
      .setOrigin(0.5)
      .setDepth(200);

    this.add
      .text(width / 2, height / 2 + 28, this.stage.clearSubtitle, {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '14px',
        color: '#a0a0c0',
        align: 'center',
        wordWrap: { width: width - 80 },
      })
      .setOrigin(0.5)
      .setDepth(200);

    const next = this.stage.nextSceneKey;
    if (this.stage.transitionDelayMs > 0) {
      this.time.delayedCall(this.stage.transitionDelayMs, () => {
        if (this.scene.key === 'TutorialScene') {
          this.registry.set('progress.tutorialClear', true);
          if (next) this.scene.start(next);
          return;
        }
        if (grantRewardPick && sid) {
          this.scene.start('StatPickScene', { stageId: sid, mode: 'reward' });
          return;
        }
        if (next) {
          this.scene.start(next);
        }
      });
    }
  }

  private applyWaveIndex(index: number): void {
    const waves = this.stage.waves;
    if (!waves?.length) return;
    const w = waves[index];
    if (!w) return;

    this.waveIndex = index;
    this.activeWaveKillTarget = w.killTarget;
    this.activeEnemySpawnMs = w.enemySpawnMs;
    this.activeEnemySpeed = w.enemySpeed;
    this.waveKills = 0;
    this.fireAccumulator = 0;
    this.spawnAccumulator = 0;

    let fm = w.fireRateMs;
    if (this.stage.applyPersistentBonuses === true) {
      const b = getPlayerBonuses(this.registry);
      fm = Math.max(110, Math.round(w.fireRateMs * b.fireRateMult));
    }
    this.effectiveFireRateMs = fm;
    this.updateWaveHeader();
    this.refreshHud();
  }

  private updateWaveHeader(): void {
    const waves = this.stage.waves;
    if (this.waveMode && waves?.length) {
      this.headerLabel.setText(
        `${this.stage.regionLabel} · 단계 ${this.waveIndex + 1}/${waves.length}`,
      );
    } else {
      this.headerLabel.setText(this.stage.regionLabel);
    }
  }

  private clearLivingCombatActors(): void {
    for (const obj of [...this.enemies.getChildren()]) {
      (obj as Phaser.GameObjects.Rectangle).destroy();
    }
    for (const obj of [...this.projectiles.getChildren()]) {
      (obj as Phaser.GameObjects.Rectangle).destroy();
    }
  }

  private beginWaveTransition(): void {
    const waves = this.stage.waves;
    if (!waves?.length) return;

    this.waveTransitioning = true;
    this.physics.pause();
    this.clearLivingCombatActors();

    const { width, height } = this.scale;
    const banner = this.add
      .text(width / 2, height / 2, `다음 단계 · ${this.waveIndex + 2} / ${waves.length}`, {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '26px',
        color: '#efe8ff',
        backgroundColor: '#2a2238ee',
        padding: { left: 24, right: 24, top: 16, bottom: 16 },
      })
      .setOrigin(0.5)
      .setDepth(250);

    this.time.delayedCall(900, () => {
      banner.destroy();
      this.applyWaveIndex(this.waveIndex + 1);
      this.waveTransitioning = false;
      this.physics.resume();
    });
  }

  private cullProjectiles(): void {
    const { width, height } = this.scale;
    const margin = 80;
    for (const obj of this.projectiles.getChildren()) {
      const p = obj as Phaser.GameObjects.Rectangle;
      if (!p.active) continue;
      if (p.x < -margin || p.y < -margin || p.x > width + margin || p.y > height + margin) {
        p.destroy();
      }
    }
  }

  private refreshHud(): void {
    const killLine = this.waveMode
      ? `처치 ${this.waveKills} / ${this.activeWaveKillTarget}`
      : `처치 ${this.kills} / ${this.stage.killTarget}`;
    this.hud.setText(`${killLine}\nHP ${this.playerHp} / ${this.playerMaxHp}`);
  }

  private dismissAbandonConfirm(resumePhysics: boolean): void {
    this.abandonOverlay.forEach((n) => n.destroy());
    this.abandonOverlay = [];
    if (!this.abandonFrozenPhysics) return;
    if (resumePhysics) this.physics.resume();
    this.abandonFrozenPhysics = false;
  }

  private openAbandonConfirm(): void {
    if (this.cleared || this.deathPending || this.abandonFrozenPhysics) return;

    this.abandonFrozenPhysics = true;
    this.physics.pause();

    const { width, height } = this.scale;
    const depth = 320;

    const backdrop = this.add
      .rectangle(width / 2, height / 2, width + 8, height + 8, 0x050308, 0.62)
      .setInteractive()
      .setScrollFactor(0)
      .setDepth(depth);

    const panelW = Math.min(400, width - 40);
    const panelH = 200;
    const panel = this.add
      .rectangle(width / 2, height / 2, panelW, panelH, 0x2a2432, 1)
      .setStrokeStyle(1, 0x5a5068)
      .setScrollFactor(0)
      .setDepth(depth + 1);

    panel.setInteractive();
    panel.on('pointerdown', () => {});

    const title = this.add
      .text(width / 2, height / 2 - 58, '전투 포기', {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '22px',
        color: '#f5f0fa',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(depth + 2);

    const msg = this.add
      .text(
        width / 2,
        height / 2 - 12,
        '거점(대기실)으로 돌아갑니다.\n진행 중인 전투는 저장되지 않습니다.',
        {
          fontFamily: 'system-ui, "Segoe UI", sans-serif',
          fontSize: '14px',
          color: '#c9c3da',
          align: 'center',
          lineSpacing: 6,
          wordWrap: { width: panelW - 40 },
        },
      )
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(depth + 2);

    const btnStyle = {
      fontFamily: 'system-ui, "Segoe UI", sans-serif',
      fontSize: '15px',
      padding: { left: 20, right: 20, top: 10, bottom: 10 },
    };

    const cancelBtn = this.add
      .text(width / 2 - 78, height / 2 + 62, '취소', {
        ...btnStyle,
        color: '#eae6f5',
        backgroundColor: '#454054cc',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .setScrollFactor(0)
      .setDepth(depth + 2);

    const okBtn = this.add
      .text(width / 2 + 78, height / 2 + 62, '거점으로', {
        ...btnStyle,
        color: '#fff8f8',
        backgroundColor: '#6b383dcc',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .setScrollFactor(0)
      .setDepth(depth + 2);

    backdrop.on('pointerdown', () => this.dismissAbandonConfirm(true));
    cancelBtn.on('pointerdown', () => this.dismissAbandonConfirm(true));
    okBtn.on('pointerdown', () => {
      this.dismissAbandonConfirm(false);
      this.scene.start('HubScene');
    });

    this.abandonOverlay.push(backdrop, panel, title, msg, cancelBtn, okBtn);
  }
}

export class TutorialScene extends CombatArenaScene {
  constructor() {
    super('TutorialScene', {
      killTarget: 22,
      playerMaxHp: 6,
      applyPersistentBonuses: false,
      regionLabel: '튜토리얼 · 빈 공간',
      playerColor: 0x6bcf7f,
      enemyColor: 0xc94c4c,
      projectileColor: 0xffdd88,
      cameraBgColor: '#12121c',
      fireRateMs: 380,
      enemySpawnMs: 1150,
      clearTitle: '튜토리얼 클리어',
      clearSubtitle: '제1층 진입 연출로 넘어갑니다…',
      nextSceneKey: 'Hell1EntryScene',
      transitionDelayMs: 2200,
    });
  }

  create(): void {
    super.create();

    const { width } = this.scale;
    const skip = this.add
      .text(width - 14, 12, '스킵', {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '15px',
        color: '#c8c8e0',
        backgroundColor: '#2a2a3acc',
        padding: { left: 10, right: 10, top: 6, bottom: 6 },
      })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(160)
      .setInteractive({ useHandCursor: true });

    skip.on('pointerover', () => skip.setAlpha(1));
    skip.on('pointerout', () => skip.setAlpha(0.92));
    skip.on('pointerdown', () => {
      this.registry.set('progress.tutorialClear', true);
      this.scene.start('Hell1EntryScene');
    });
    skip.setAlpha(0.92);
  }
}

/** 지옥 제1층 1-1 ~ 1-5 전투 — `init({ stageId })` 로 구역 지정 */
export class Hell1CombatScene extends CombatArenaScene {
  constructor() {
    super('Hell1CombatScene', getHell1ArenaConfig('1-1'));
  }

  init(data: { stageId?: string }): void {
    const id = data?.stageId ?? '1-1';
    this.stage = getHell1ArenaConfig(id);
  }

  protected override getSceneRestartPayload(): object | undefined {
    const id = this.stage.hellStageId;
    return id !== undefined ? { stageId: id } : undefined;
  }
}
